import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2, PackageX, Package, MapPin, Weight, Box, Calendar, Truck, CheckCircle2, Clock, AlertCircle, Ship, Plane, Star, Wallet, FileText } from "lucide-react";
import { API } from "@/lib/api";
import { useT } from "@/context/LangContext";
import { ProductGallery } from "@/components/ProductGallery";

const STATUS_ICON = {
  created: Package,
  picked_up: Truck,
  in_transit_china: Truck,
  in_transit: Ship,
  arrived_port: Ship,
  customs_clearance: Clock,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  exception: AlertCircle,
};

function formatDate(iso, lang = "fr") {
  try {
    const locale = { fr: "fr-FR", en: "en-US", zh: "zh-CN" }[lang] || "fr-FR";
    return new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function Tracking() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const initial = params.get("n") || "";
  const [tn, setTn] = useState(initial);
  const [state, setState] = useState({ loading: false, data: null, err: "" });

  const fetchTracking = async (num) => {
    setState({ loading: true, data: null, err: "" });
    try {
      const r = await fetch(`${API}/tracking/${encodeURIComponent(num)}`);
      if (r.status === 404) { setState({ loading: false, data: null, err: t("freight.track.notfound") }); return; }
      if (!r.ok) { setState({ loading: false, data: null, err: `Error ${r.status}` }); return; }
      const data = await r.json();
      setState({ loading: false, data, err: "" });
    } catch (e) {
      setState({ loading: false, data: null, err: String(e) });
    }
  };

  useEffect(() => { if (initial) fetchTracking(initial); /* eslint-disable-next-line */ }, []);

  const submit = (e) => {
    e.preventDefault();
    const v = tn.trim().toUpperCase();
    if (!v) return;
    setParams({ n: v });
    fetchTracking(v);
  };

  const data = state.data;
  const ServiceIcon = data?.service_type === "express" ? Plane : Ship;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="tracking-page">
      <p className="kicker">{t("freight.track.title")}</p>
      <h1 className="font-display text-4xl lg:text-6xl font-black tracking-tighter mt-4">
        {t("nav.tracking")}
      </h1>
      <p className="text-[var(--ink-soft)] mt-4">{t("freight.track.desc")}</p>

      <form onSubmit={submit} className="mt-8 flex flex-col md:flex-row gap-3">
        <input
          value={tn}
          onChange={(e) => setTn(e.target.value)}
          placeholder={t("freight.track.placeholder")}
          className="input-field flex-1 font-mono uppercase tracking-widest"
          data-testid="tracking-input"
        />
        <button type="submit" className="btn-primary justify-center" disabled={state.loading} data-testid="tracking-submit">
          {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} {t("freight.track.btn")}
        </button>
      </form>

      {state.err && (
        <div className="mt-8 card-flat p-8 text-center" data-testid="tracking-notfound">
          <PackageX className="mx-auto text-red-500" size={40} />
          <p className="mt-4 text-[var(--ink)]">{state.err}</p>
        </div>
      )}

      {data && (
        <>
          <div className="mt-10 card-flat p-6 lg:p-8" data-testid="tracking-summary">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="kicker">{t("freight.track.summary")}</p>
                <p className="font-display text-2xl font-black mt-2 font-mono">{data.tracking_number}</p>
              </div>
              <div className="flex items-center gap-3 bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[var(--accent)] px-4 py-2 rounded-full">
                <ServiceIcon size={16} />
                <span className="text-xs font-bold uppercase tracking-widest">
                  {t(`freight.${data.service_type || "normal"}.title`)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8">
              <Info icon={MapPin} label={t("freight.track.origin")} value={data.origin} />
              <Info icon={MapPin} label={t("freight.track.dest")} value={data.destination} />
              <Info icon={Calendar} label={t("freight.track.eta")} value={data.estimated_delivery || "—"} />
              {data.weight_kg != null && <Info icon={Weight} label={t("freight.track.weight")} value={`${data.weight_kg} kg`} />}
              {data.volume_cbm != null && <Info icon={Box} label={t("freight.track.volume")} value={`${data.volume_cbm} m³`} />}
              <Info icon={Package} label={t("freight.track.description")} value={data.description || "—"} />
            </div>

            <div className="mt-8 pt-6 border-t border-black/5">
              <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{t("freight.track.status")}</p>
              <p className="font-display text-2xl font-bold mt-1 text-[var(--accent)]">
                {t(`status.${data.current_status}`)}
              </p>
              {data.current_location && (
                <p className="text-sm text-[var(--ink-soft)] mt-1">📍 {data.current_location}</p>
              )}
            </div>

            {data.amount_due != null && (
              <div className="mt-6 rounded-xl bg-gradient-to-r from-[var(--accent)] to-[#D62828] p-6 text-white shadow-lg" data-testid="tracking-amount">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-widest opacity-90 flex items-center gap-2"><Wallet size={13} /> À payer à l'arrivée</p>
                    <p className="font-display text-4xl font-black mt-2">
                      {Math.round(Number(data.amount_due)).toLocaleString("fr-FR")} <span className="text-lg font-bold">{data.amount_currency || "FCFA"}</span>
                    </p>
                    <p className="text-xs opacity-90 mt-1">Règlement à la livraison — aucune surprise</p>
                  </div>
                  <a href={`${API}/shipments/${data.tracking_number}/receipt.pdf`} target="_blank" rel="noopener noreferrer" className="bg-white text-[var(--accent)] font-bold text-xs px-3 py-2 rounded inline-flex items-center gap-1.5">
                    <FileText size={13} /> PDF
                  </a>
                </div>
              </div>
            )}
          </div>

          {(data.photos || []).length > 0 && (
            <div className="mt-8" data-testid="tracking-photos">
              <p className="kicker">Vos produits</p>
              <p className="text-sm text-[var(--ink-soft)] mt-1">{data.photos.length} photo{data.photos.length > 1 ? "s" : ""} de la marchandise</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {data.photos.map((url, i) => (
                  <div key={url + i} className="aspect-square rounded-lg overflow-hidden bg-[var(--bg-soft)] card-flat group">
                    <ProductGallery images={[url]} alt={`Produit ${i + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8" data-testid="tracking-timeline">
            <p className="kicker">{t("freight.track.timeline")}</p>
            <div className="mt-6 relative">
              <div className="absolute left-4 top-2 bottom-2 w-px bg-black/10" />
              <ul className="space-y-6">
                {[...(data.events || [])].reverse().map((ev) => {
                  const Icon = STATUS_ICON[ev.status] || Package;
                  return (
                    <li key={ev.id} className="relative pl-12" data-testid={`event-${ev.id}`}>
                      <span className="absolute left-0 top-0 w-8 h-8 rounded-full bg-white border-2 border-[var(--accent)] text-[var(--accent)] flex items-center justify-center">
                        <Icon size={14} />
                      </span>
                      <p className="font-bold">{t(`status.${ev.status}`)}</p>
                      <p className="text-xs text-[var(--ink-soft)]">{formatDate(ev.at)}</p>
                      {ev.location && <p className="text-sm mt-1 text-[var(--ink-soft)]">📍 {ev.location}</p>}
                      {ev.note && <p className="text-sm mt-1">{ev.note}</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <RatingBlock data={data} onRated={(r) => setState((s) => ({ ...s, data: { ...s.data, rating: r.stars } })) } />
        </>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] flex items-center gap-2">
        <Icon size={12} /> {label}
      </p>
      <p className="font-bold mt-1 text-sm">{value}</p>
    </div>
  );
}

function RatingBlock({ data, onRated }) {
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [state, setState] = useState({ loading: false, err: "", success: false });

  const submitted = data.rating != null;

  const submit = async () => {
    if (!stars) return;
    setState({ loading: true, err: "", success: false });
    try {
      const r = await fetch(`${API}/tracking/${encodeURIComponent(data.tracking_number)}/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stars, comment: comment.trim() || null }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Erreur ${r.status}`);
      }
      setState({ loading: false, err: "", success: true });
      onRated?.({ stars });
    } catch (e) {
      setState({ loading: false, err: String(e.message || e), success: false });
    }
  };

  if (submitted || state.success) {
    const shown = submitted ? data.rating : stars;
    return (
      <div className="mt-8 card-flat p-6 text-center bg-[var(--bg-soft)]/50" data-testid="tracking-rating-done">
        <p className="kicker !text-[var(--secondary)]">Votre avis</p>
        <div className="mt-3 flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={24} className={n <= shown ? "text-yellow-500 fill-yellow-500" : "text-black/15"} />
          ))}
        </div>
        <p className="text-sm text-[var(--ink-soft)] mt-3">Merci pour votre note ! Cela nous aide à améliorer notre service.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 card-flat p-6" data-testid="tracking-rating">
      <p className="kicker !text-[var(--secondary)]">Notez notre service</p>
      <p className="text-sm text-[var(--ink-soft)] mt-1">Votre avis nous aide à mieux vous servir.</p>
      <div className="mt-4 flex gap-1 justify-center" data-testid="rating-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setStars(n)}
            className="p-1"
            aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
            data-testid={`rating-star-${n}`}
          >
            <Star size={32} className={n <= (hover || stars) ? "text-yellow-500 fill-yellow-500 transition-transform hover:scale-110" : "text-black/15 hover:text-black/30"} />
          </button>
        ))}
      </div>
      <textarea
        placeholder="Un mot pour nous ? (optionnel)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="input-field mt-4 min-h-[80px] w-full"
        maxLength={500}
        data-testid="rating-comment"
      />
      {state.err && <p className="text-sm text-red-500 mt-2">{state.err}</p>}
      <button
        onClick={submit}
        disabled={!stars || state.loading}
        className="btn-primary w-full mt-4 justify-center disabled:opacity-50"
        data-testid="rating-submit"
      >
        {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
        {stars ? `Envoyer ma note (${stars}/5)` : "Choisissez une note"}
      </button>
    </div>
  );
}
